import React from "react";
import PropTypes from "prop-types";
import Link from "next/link";
import Image from "next/image";
import styled, { useTheme } from "styled-components";
import { unescape } from "lodash";
import Location from "components/icons/location";
import Button from "components/Button";
import generateRandomImagePlaceholderPath from "lib/generate-random-image-placeholder";
import YotpoStarRatingsWidget from "components/YotPoReviewsWidget/StarRating";

const OuterCardWrapper = styled.li`
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  list-style-type: none;
  margin-right: 1rem;
  width: 18.3125rem;

  ${({ theme }) => theme.mq.md`
    width: 33%;
    min-width: 21.375rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0 3.125rem 0 0;
    width: 22.375rem;
  `}
  
  &:last-child {
    margin-right: 0;
  }
`;

const InnerCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BizImage = styled.div`
  border: 0.1875rem solid transparent;
  border-radius: 0.5rem;
  height: 13.75rem;
  margin-bottom: 0.75rem;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primary};
  }

  ${({ theme }) => theme.mq.xxl`
    height: 12rem;
  `}
`;

const BizName = styled.h4`
  color: ${({ theme }) => theme.themeColors.darkGrey};
  font-size: 1.125rem;
  font-family: ${({ theme }) => theme.fontFamily};
  font-weight: 600;
  letter-spacing: -0.025rem;
  line-height: 1.8125rem;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 0.1875rem;
`;

const YotpoStarRatingsWrapper = styled.div`
  margin-bottom: 0.875rem;
`;

const BizAttribute = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSize(1)};
  font-weight: 400;
  line-height: 1.125rem;
  max-height: 1.125rem;
  margin-bottom: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    margin-bottom: 0.1875rem;
  }
`;

const Address = styled.address`
  color: ${({ theme }) => theme.themeColors.mediumGrey};
  display: inline;
  font-weight: 400;
`;

const LocationIcon = styled(Location)`
  height: 0.9375rem;
  margin-right: 0.1875rem;
  width: 0.9375rem;
`;

const CheckAvailabilityButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSize(0)};
  border: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  height: 2.375rem;
  line-height: 1;
  margin-bottom: 0.5rem;
  width: 8.125rem;

  ${({ theme }) => theme.mq.xxl`
    margin-bottom: 0;
  `}
`;

function HomepageFeaturedBusinessCardComponent({ business }) {
  const {
    attributes,
    include: { serviceTypes },
  } = business;
  const {
    businessName,
    friendlyName,
    urlLocality,
    urlRegion,
    averageYotpoScore,
    totalYotpoReviews,
  } = attributes;
  const types = serviceTypes.data;

  const formattedAddress =
    business?.attributes?.addressPayload?.formatted_address;

  const businessImage =
    business.attributes.logo?.sizes?.medium?.url ||
    generateRandomImagePlaceholderPath(business.id, types);

  const theme = useTheme();

  return (
    <OuterCardWrapper>
      <Link
        href={`/business/${urlRegion}/${urlLocality}/${friendlyName}`}
        passHref
      >
        <a>
          <InnerCardWrapper>
            <BizImage>
              <Image
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                loading="lazy"
                src={businessImage}
                alt={businessName}
              />
            </BizImage>
            <DescriptionWrapper>
              <BizName>{unescape(businessName)}</BizName>
              {totalYotpoReviews > 0 && (
                <YotpoStarRatingsWrapper>
                  <YotpoStarRatingsWidget
                    averageYotpoScore={averageYotpoScore}
                    totalYotpoReviews={totalYotpoReviews}
                  />
                </YotpoStarRatingsWrapper>
              )}
              {formattedAddress && (
                <BizAttribute>
                  <LocationIcon color={theme.themeColors.mediumGrey} />
                  <Address>{formattedAddress}</Address>
                </BizAttribute>
              )}
              <Link
                href={`/business/${urlRegion}/${urlLocality}/${friendlyName}`}
                passHref
              >
                <CheckAvailabilityButton
                  type="button"
                  outlineType="outline"
                  colorType="black"
                >
                  Check availability
                </CheckAvailabilityButton>
              </Link>
            </DescriptionWrapper>
          </InnerCardWrapper>
        </a>
      </Link>
    </OuterCardWrapper>
  );
}

export default HomepageFeaturedBusinessCardComponent;

HomepageFeaturedBusinessCardComponent.propTypes = {
  business: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.object,
    include: PropTypes.shape({
      serviceTypes: PropTypes.shape({
        data: PropTypes.array,
      }),
      vendorBusinessOpenHours: PropTypes.shape({
        data: PropTypes.oneOfType(
          [
            PropTypes.arrayOf(
              PropTypes.shape({
                attributes: PropTypes.shape({
                  calcStartTime: PropTypes.string,
                  calcEndTime: PropTypes.string,
                }),
              })
            ),
          ],
          PropTypes.shape({
            attributes: PropTypes.shape({
              calcStartTime: PropTypes.string,
              calcEndTime: PropTypes.string,
            }),
          })
        ),
      }),
    }),
  }).isRequired,
};

HomepageFeaturedBusinessCardComponent.defaultProps = {};
